import { useArtifactGate, GateLock } from './ArtifactGate';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import FieldListSidebar, { SidebarBadge } from './FieldListSidebar';
import { UIFieldCoordinates as FieldCoordinates } from '../types/codeGeneration';
import { FieldSortPrefs, ReadingOrderMode, sortFields } from '../utils/fieldSort';
import { autoFontSize, previewFieldStyle } from '../utils/fieldFonts';
import { characterBlockPattern, resolveCharacterBlockGroups } from '../utils/characterBlockGroups';
import { fillKeyFor } from '../utils/fieldDataBinding';

interface LiveFormPreviewProps {
  fields: FieldCoordinates[];
  formName: string;
  onFieldChange?: (fieldId: string, value: any) => void;
  onGenerateFillablePDF?: (formData: any) => void;
  onGenerateFlattenedPDF?: (formData: any) => void;
  /** Free look at the flattened result. Ungated - see design section 3.3.1. */
  onPreviewFlattenedPDF?: (formData: any) => void;
  onGenerateSpringBootProject?: (formData: any) => void;
  onSubmitForm?: (formData: any) => void;
  fieldSortPrefs: FieldSortPrefs; // Shared with the editor sidebar (lifted to PDFUpload). A VIEW.
  onFieldSortPrefsChange: (prefs: FieldSortPrefs) => void;
  /** For DISPLAY only here. The preview's sidebar has no reorder handler, so it
   *  reports which order the document will get rather than offering to change
   *  it - one editing surface for one fact (D37). */
  readingOrderMode: ReadingOrderMode;
}

interface FormData {
  [fieldId: string]: any;
}

/**
 * Has this field been answered?
 *
 * ONE definition, shared by the sidebar badge, the flattened-PDF gate and
 * `validateForm`, so the count printed beside the Generate Flattened PDF button
 * can never disagree with what actually blocks that button.
 */
const hasValue = (field: FieldCoordinates, value: any): boolean =>
  field.type === 'checkbox'
    ? value === true
    : value !== undefined && value !== null && String(value).trim() !== '';

/**
 * The per-field note for an empty required field.
 *
 * Worded as a fact about the FIELD rather than a scolding, and naming the one
 * output it actually blocks - see the asymmetry note in the component below.
 */
const EMPTY_REQUIRED_FIELD_NOTE = 'Required before a flattened PDF can be generated';

const LiveFormPreview: React.FC<LiveFormPreviewProps> = ({
  fields,
  formName,
  onFieldChange,
  onGenerateFillablePDF,
  onGenerateFlattenedPDF,
  onPreviewFlattenedPDF,
  onGenerateSpringBootProject,
  onSubmitForm,
  fieldSortPrefs,
  onFieldSortPrefsChange,
  readingOrderMode
}) => {
  const { guard, allows } = useArtifactGate();
  const [formData, setFormData] = useState<FormData>({});
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingFillablePDF, setIsGeneratingFillablePDF] = useState(false);
  const [isGeneratingFlattenedPDF, setIsGeneratingFlattenedPDF] = useState(false);
  const [isGeneratingSpringBootProject, setIsGeneratingSpringBootProject] = useState(false);
  const [pdfGenerationStatus, setPdfGenerationStatus] = useState<{
    message: string;
    type: 'success' | 'error' | 'info' | '';
  }>({ message: '', type: '' });
  // Sample-payload panel (demoted from the old Test Form Submission button)
  const [showSubmissionJson, setShowSubmissionJson] = useState(false);
  // Sidebar-driven focus: highlighted card + scroll/focus target
  const [focusedFieldId, setFocusedFieldId] = useState<string | null>(null);
  const fieldCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Character-block inputs, keyed "<fieldId>:<blockIndex>", so typing can
  // advance to the next box without reaching through the DOM mid-render.
  const blockInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Same ordering as the editor sidebar and the generated project (WYSIWYG)
  const orderedFields = useMemo(() => sortFields(fields, fieldSortPrefs), [fields, fieldSortPrefs]);

  // Preview badges: has the user entered a value yet? Required-but-empty gets
  // an amber nudge so completion state is scannable from the sidebar.
  const previewBadgeById = useMemo(() => {
    const badges: Record<string, SidebarBadge> = {};
    fields.forEach(field => {
      const value = formData[fillKeyFor(field)];
      const filled = hasValue(field, value);
      if (filled) {
        badges[field.id] = { label: 'Filled', className: 'bg-green-100 text-green-800', title: 'Has a value' };
      } else if (field.required) {
        badges[field.id] = { label: 'Req.', className: 'bg-amber-100 text-amber-800', title: 'Required — no value yet' };
      } else {
        badges[field.id] = { label: 'Empty', className: 'bg-gray-100 text-gray-600', title: 'No value yet' };
      }
    });
    return badges;
  }, [fields, formData]);

  // ==========================================================================
  // WHY `required` IS ENFORCED ON ONE OUTPUT AND NOT THE OTHERS
  //
  // Flattened generation is the last moment `required` can mean anything.
  // After that it is ink.
  //
  // A FILLABLE PDF *defers* the answer. The field survives into the document
  // carrying /Ff bit 2, and the RECIPIENT's reader is what enforces it, at the
  // moment they fill it in. An author who leaves every box empty has not made
  // a mistake - that IS the blank form for distribution, which is the primary
  // use of this product. Pre-filling changes nothing either way: the recipient
  // can still edit the field, and the flag still applies to them.
  //
  // A FLATTENED PDF *finalises* the answer. No field survives flattening, so a
  // required question left unanswered becomes permanently unanswerable - nobody
  // downstream can supply it, ever. `PdfUaFlattenedTagger` has no notion of
  // `required` at all, and that is correct rather than an oversight:
  // generation time is the only place the obligation can still be met, so this
  // is the only place it can be checked.
  //
  // A SPRING BOOT PROJECT is emitted from the field DEFINITIONS. Not one
  // character typed into this preview reaches the generated Java, so refusing
  // to emit an application because a preview box is empty was never defensible.
  //
  // Hence the split: submission and flattened generation require values;
  // fillable generation and project generation do not. Everything else
  // `validateForm` checks - invalid field names, malformed values - is a
  // genuine generation blocker and is unchanged on every path.
  // ==========================================================================
  //
  // In `orderedFields` order, so "the first empty one" is the first one a
  // reader meets rather than the first one in an arbitrary array.
  const emptyRequiredFields = useMemo(
    () => orderedFields.filter(field => field.required && !hasValue(field, formData[fillKeyFor(field)])),
    [orderedFields, formData]
  );
  const hasEmptyRequired = emptyRequiredFields.length > 0;

  // The sentence is the whole argument for the asymmetry, so it is placed where
  // it is read: beside the button, and as that button's accessible description.
  const emptyRequiredSentence = emptyRequiredFields.length === 1
    ? '1 required field is empty. A flattened PDF cannot be filled in later.'
    : `${emptyRequiredFields.length} required fields are empty. A flattened PDF cannot be filled in later.`;

  const sidebarDisplayName = (field: FieldCoordinates, originalIndex: number) => {
    if (field.name === 'loading...' || !field.name || field.name.startsWith('new_field_')) {
      return `Field ${originalIndex + 1}`;
    }
    return field.name;
  };

  // Sidebar click: highlight the card, scroll it into view, focus its input.
  const handleSidebarFieldSelect = (fieldId: string) => {
    setFocusedFieldId(fieldId);
    fieldCardRefs.current[fieldId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => document.getElementById(fieldId)?.focus({ preventScroll: true }), 350);
  };

  // Clicking Generate Flattened PDF while it is `aria-disabled` lands here.
  //
  // The D8 error-summary pattern, one field wide: a control that refuses must
  // say WHY and take you to the cause. A tooltip cannot do the second half, and
  // a real `disabled` button cannot do either - it leaves the tab order and
  // swallows the click, so a screen-reader user never learns the control is
  // there. This is the ONLY deliberate focus move on this surface (D30).
  const focusFirstEmptyRequired = () => {
    const field = emptyRequiredFields[0];
    if (!field) return;
    setFocusedFieldId(field.id);
    fieldCardRefs.current[field.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // A character block renders one input PER BOX and no element carries the
    // field id, so the first box is the landing place. A required checkbox
    // currently renders as a non-focusable <div> and has nothing to land on at
    // all (registered as D60) - its card is still scrolled into view and the
    // live region still announces the count, which is as far as this can go
    // without adding a `tabindex`, which is forbidden here.
    const target = (document.getElementById(field.id)
      || blockInputRefs.current[`${field.id}:0`]) as HTMLElement | null;
    target?.focus({ preventScroll: true });
  };

  // Seed form data from the fields, MERGING rather than replacing (QA 2026-08-18).
  //
  // Two bugs came from the old replace-everything version, which re-ran whenever the
  // set of field ids changed:
  //   1. Adding or deleting one field in the editor wiped every value already typed
  //      into the preview. (It used to survive by accident, because each keystroke was
  //      mirrored into field.defaultValue — that mirroring was removed in d6cb3fa
  //      because it dirtied the session and would have persisted sample data.)
  //   2. Editing a field's Default Value in the properties sidebar never reached the
  //      preview input or its Filled/Empty badge, because nothing re-seeded that id.
  //
  // So: a value the user typed here wins and is preserved; an untouched field follows
  // its Default Value, including when that default is edited; removed fields drop out.
  // Keyed on id + type + defaultValue so a default edit re-seeds, and touch state for
  // vanished fields is forgotten.
  const touchedFieldsRef = useRef<Set<string>>(new Set());
  const seededValueFor = (field: FieldCoordinates) =>
    field.type === 'checkbox' ? field.defaultValue === 'true' : (field.defaultValue ?? '');
  const fieldSeedKey = fields.map(f => `${f.id}|${fillKeyFor(f)}|${f.type}|${f.defaultValue ?? ''}`).join(',');
  useEffect(() => {
    setFormData(prev => {
      const next: FormData = {};
      fields.forEach(field => {
        const k = fillKeyFor(field);
        next[k] = touchedFieldsRef.current.has(k) && k in prev
          ? prev[k]
          : seededValueFor(field);
      });
      Array.from(touchedFieldsRef.current).forEach(id => {
        if (!(id in next)) touchedFieldsRef.current.delete(id);
      });
      return next;
    });
  }, [fieldSeedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track field array length changes specifically for deletions
  useEffect(() => {
    console.log('LiveFormPreview: Field array length changed to:', fields.length);
    console.log('LiveFormPreview: Current field IDs in array:', fields.map(f => f.id));
  }, [fields.length]);

  // Handle field value changes
  const handleFieldChange = (field: FieldCoordinates, value: any) => {
    // Remember that the user owns this field's value, so a later re-seed (another
    // field added, a default edited elsewhere) does not overwrite what they typed.
    // Keyed by the FILL key so fields sharing a dataKey share one value.
    const key = fillKeyFor(field);
    touchedFieldsRef.current.add(key);
    setFormData(prev => ({ ...prev, [key]: value }));

    // Clear validation error for this field (per-widget, so keyed by field.id).
    if (validationErrors[field.id]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field.id];
        return newErrors;
      });
    }

    // Notify parent component (contract unchanged: reports field.id).
    if (onFieldChange) {
      onFieldChange(field.id, value);
    }
  };

  /**
   * Validate the preview's entered values.
   *
   * @param requireValues Whether an empty REQUIRED field counts as an error on
   *   this path. TRUE for submission and for flattened generation; FALSE for
   *   fillable generation, where a blank required field is the point of the
   *   artefact rather than a defect in it. See the asymmetry note above -
   *   flattened generation is the last moment `required` can mean anything.
   *
   *   Every OTHER check below runs regardless, because each is a genuine
   *   blocker: a malformed email, number or date would be written into the
   *   document as-is.
   */
  const validateForm = (requireValues: boolean = true): boolean => {
    const errors: {[key: string]: string} = {};
    let isValid = true;

    fields.forEach(field => {
      const value = formData[fillKeyFor(field)];

      // Required field validation - gated, see `requireValues` above. Uses the
      // SAME `hasValue` the button count uses, so the two cannot drift apart.
      if (requireValues && field.required && !hasValue(field, value)) {
        errors[field.id] = EMPTY_REQUIRED_FIELD_NOTE;
        isValid = false;
      }

      // Type-specific validation
      if (value && value.toString().trim() !== '') {
        switch (field.type) {
          case 'email':
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
              errors[field.id] = 'Please enter a valid email address';
              isValid = false;
            }
            break;
          case 'number':
            if (isNaN(Number(value))) {
              errors[field.id] = 'Please enter a valid number';
              isValid = false;
            }
            break;
          case 'date':
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(value)) {
              errors[field.id] = 'Please enter a valid date (YYYY-MM-DD)';
              isValid = false;
            }
            break;
        }
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setPdfGenerationStatus({
        message: 'Please fix the validation errors before submitting the form',
        type: 'error'
      });
      return;
    }

    setIsSubmitting(true);
    setPdfGenerationStatus({
      message: 'Submitting form...',
      type: 'info'
    });
    
    try {
      if (onSubmitForm) {
        await onSubmitForm(formData);
        setPdfGenerationStatus({
          message: 'Form submitted successfully! Data has been logged to the console.',
          type: 'success'
        });
        console.log('✅ Form Submission Data:', formData);
        console.log('✅ Number of fields submitted:', Object.keys(formData).length);
        console.log('✅ Field names and values:', Object.entries(formData));
      } else {
        setPdfGenerationStatus({
          message: 'No form submission handler available',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('❌ Error submitting form:', error);
      setPdfGenerationStatus({
        message: `Form submission failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
      
      // Clear status after 5 seconds for success/info messages
      if (pdfGenerationStatus.type !== 'error') {
        setTimeout(() => {
          setPdfGenerationStatus({ message: '', type: '' });
        }, 5000);
      }
    }
  };

  // Handle fillable PDF generation
  const handleGenerateFillablePDF = async () => {
    // Basic validation - ensure all fields have valid names
    const invalidFields = fields.filter(field => 
      !field.name || field.name.trim() === ''
    );
    
    if (invalidFields.length > 0) {
      setPdfGenerationStatus({
        message: `Cannot generate PDF: ${invalidFields.length} field(s) have invalid names. Please ensure all fields have proper names.`,
        type: 'error'
      });
      return;
    }

    // `false` = do NOT require values. A fillable PDF with empty required
    // fields is not a broken document, it is the BLANK FORM FOR DISTRIBUTION -
    // the field carries /Ff bit 2 into the PDF and the recipient's reader
    // enforces it when they fill it in. The format checks still run: a
    // malformed date typed here would be written into the document as-is.
    if (!validateForm(false)) {
      setPdfGenerationStatus({
        message: 'Please fix the validation errors before generating PDF',
        type: 'error'
      });
      return;
    }

    setIsGeneratingFillablePDF(true);
    setPdfGenerationStatus({
      message: 'Generating fillable PDF with editable fields...',
      type: 'info'
    });

    try {
      if (onGenerateFillablePDF) {
        await onGenerateFillablePDF(formData);
        setPdfGenerationStatus({
          message: 'Fillable PDF generated successfully! Check your Downloads folder.',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error generating fillable PDF:', error);
      setPdfGenerationStatus({
        message: 'Failed to generate fillable PDF. Please try again.',
        type: 'error'
      });
    } finally {
      setIsGeneratingFillablePDF(false);
      // Clear status after 5 seconds
      setTimeout(() => {
        setPdfGenerationStatus({ message: '', type: '' });
      }, 5000);
    }
  };

  // Handle flattened PDF generation
  const handleGenerateFlattenedPDF = async () => {
    // Basic validation - ensure all fields have valid names
    const invalidFields = fields.filter(field => 
      !field.name || field.name.trim() === ''
    );
    
    if (invalidFields.length > 0) {
      setPdfGenerationStatus({
        message: `Cannot generate PDF: ${invalidFields.length} field(s) have invalid names. Please ensure all fields have proper names.`,
        type: 'error'
      });
      return;
    }

    // FULL validation, values included - flattening is the last moment
    // `required` can mean anything. The button is already `aria-disabled` in
    // this state and sends the reader to the offending field; this is the
    // backstop for any other route into this handler.
    if (!validateForm()) {
      setPdfGenerationStatus({
        message: 'Please fix the validation errors before generating PDF',
        type: 'error'
      });
      return;
    }

    setIsGeneratingFlattenedPDF(true);
    setPdfGenerationStatus({
      message: 'Generating flattened PDF with permanent text...',
      type: 'info'
    });

    try {
      if (onGenerateFlattenedPDF) {
        await onGenerateFlattenedPDF(formData);
        setPdfGenerationStatus({
          message: 'Flattened PDF generated successfully! Check your Downloads folder.',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error generating flattened PDF:', error);
      setPdfGenerationStatus({
        message: 'Failed to generate flattened PDF. Please try again.',
        type: 'error'
      });
    } finally {
      setIsGeneratingFlattenedPDF(false);
      // Clear status after 5 seconds
      setTimeout(() => {
        setPdfGenerationStatus({ message: '', type: '' });
      }, 5000);
    }
  };

  // Handle Spring Boot project generation
  const handleGenerateSpringBootProject = async () => {
    // Basic validation - ensure all fields have valid names
    const invalidFields = fields.filter(field => 
      !field.name || field.name.trim() === ''
    );
    
    if (invalidFields.length > 0) {
      setPdfGenerationStatus({
        message: `Cannot generate Spring Boot project: ${invalidFields.length} field(s) have invalid names. Please ensure all fields have proper names.`,
        type: 'error'
      });
      return;
    }

    // NO value validation here, deliberately. The project is emitted from the
    // field DEFINITIONS - names, types, coordinates, the `required` flag itself
    // (which becomes @NotBlank in the generated entity and `required` on the
    // generated web form). Nothing typed into this preview reaches the emitted
    // Java, so refusing to generate an application because a preview box is
    // empty - or holds a half-typed email address - was never defensible.
    // The invalid-NAME check above stays: a field name becomes a Java
    // identifier and a database column, and an empty one genuinely cannot be
    // generated.

    setIsGeneratingSpringBootProject(true);
    setPdfGenerationStatus({
      message: 'Generating complete Spring Boot project with REST API...',
      type: 'info'
    });

    try {
      if (onGenerateSpringBootProject) {
        await onGenerateSpringBootProject(formData);
        setPdfGenerationStatus({
          message: 'Spring Boot project generated successfully! Check your Downloads folder.',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error generating Spring Boot project:', error);
      setPdfGenerationStatus({
        message: 'Failed to generate Spring Boot project. Please try again.',
        type: 'error'
      });
    } finally {
      setIsGeneratingSpringBootProject(false);
      // Clear status after 5 seconds
      setTimeout(() => {
        setPdfGenerationStatus({ message: '', type: '' });
      }, 5000);
    }
  };


  // Render individual form field
  const renderField = (field: FieldCoordinates) => {
    const value = formData[fillKeyFor(field)] || '';
    const hasError = validationErrors[field.id];
    const fieldLabel = field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    const baseInputClasses = `w-full px-3 py-2 border rounded-md transition-colors duration-200 ${
      hasError
        ? 'border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500'
        : 'border-gray-300 bg-white focus:ring-blue-500 focus:border-blue-500'
    } focus:outline-none focus:ring-2`;
    // Drawn width + the font the PDF will actually use, applied inline so the
    // preview represents the document rather than a generic web form.
    const fontStyle = previewFieldStyle(field, field.type === 'textarea');

    switch (field.type) {
      case 'textarea': {
        // Multi-line entry sized from the drawn field: rows ~= field height
        // over the line height (fontSize * 1.2 leading, matching the PDF
        // renderers), clamped 2..10 for readability. Previously fell through
        // to the single-line text input.
        const taFontSize = field.fontSize ?? autoFontSize(field.height, true);
        const taRows = Math.min(10, Math.max(2, Math.round(field.height / (taFontSize * 1.2))));
        return (
          <textarea
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            placeholder={field.formatHint || `Enter ${fieldLabel}`}
            rows={taRows}
            className={`${baseInputClasses} resize-y`} style={fontStyle}
          />
        );
      }

      case 'text':
        return (
          <input
            type="text"
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            placeholder={field.formatHint || `Enter ${fieldLabel}`}
            className={baseInputClasses} style={fontStyle}
          />
        );

      case 'email':
        return (
          <input
            type="email"
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            placeholder={field.formatHint || 'Enter email address'}
            className={baseInputClasses} style={fontStyle}
          />
        );

      case 'number':
        return (
          <input
            type="number" onWheel={(e) => e.currentTarget.blur()}
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            placeholder={field.formatHint || 'Enter number'}
            className={baseInputClasses} style={fontStyle}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            className={baseInputClasses} style={fontStyle}
          />
        );

      case 'checkbox':
        const isChecked = Boolean(value);
        return (
          <div
            className="flex items-center cursor-pointer"
            onClick={() => handleFieldChange(field, !isChecked)}
            style={{ minHeight: '28px' }}
          >
            <div style={{
              // fontSize override scales the preview check box (mirrors the PDF
              // mark-size override); default stays the fixed 22px box
              width: `${field.fontSize && field.fontSize > 0 ? field.fontSize + 6 : 22}px`,
              height: `${field.fontSize && field.fontSize > 0 ? field.fontSize + 6 : 22}px`,
              borderRadius: '4px',
              border: isChecked ? '2px solid #2563eb' : '2px solid #9ca3af',
              backgroundColor: isChecked ? '#2563eb' : 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'all 0.15s ease',
              cursor: 'pointer'
            }}>
              {isChecked && (
                <svg
                  width={field.fontSize && field.fontSize > 0 ? Math.round((field.fontSize + 6) * 0.64) : 14}
                  height={field.fontSize && field.fontSize > 0 ? Math.round((field.fontSize + 6) * 0.64) : 14}
                  viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              )}
            </div>
            <span className="ml-2 text-sm text-gray-700" style={{ cursor: 'pointer' }}>
              {fieldLabel}
            </span>
          </div>
        );

      case 'select':
        const selectOptions = field.selectConfig?.options || [];
        return (
          <select
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            className={baseInputClasses} style={fontStyle}
          >
            <option value="">-- Select --</option>
            {selectOptions.length > 0
              ? selectOptions.map((opt: any, i: number) => (
                  <option key={i} value={opt.value || opt.label}>
                    {opt.label || opt.value}
                  </option>
                ))
              : <>
                  <option value="option1">Option 1</option>
                  <option value="option2">Option 2</option>
                  <option value="option3">Option 3</option>
                </>
            }
          </select>
        );

      case 'character_block':
        if (!field.characterBlockConfig) {
          return (
            <input
              type="text"
              id={field.id}
              value={value}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              placeholder={field.formatHint || `Enter ${fieldLabel}`}
              className={baseInputClasses} style={fontStyle}
            />
          );
        }

        // Only what the on-screen layout needs. blockWidth, blockHeight and
        // blockSpacing are deliberately NOT destructured any more - the boxes
        // are normalised (see below), and pulling them in here is what invited
        // three rounds of trying to reproduce PDF geometry in CSS. They are
        // still shown verbatim in the field's info panel, where they belong.
        const { 
          totalBlocks, 
          firstGap = 0,
          firstGapPosition,
          secondGap = 0,
          secondGapPosition
        } = field.characterBlockConfig;

        // NORMALISED, deliberately. The PDF-derived blockWidth/blockHeight
        // are NOT used for on-screen sizing.
        //
        // Chasing them produced three bugs in a row: boxes too narrow for their
        // own glyph, a 48px height that only looked right by accident, and a
        // content-box that clipped the character out of view. Each fix exposed
        // the next, because the premise was wrong. A block is ONE CHARACTER in
        // a drawn cell - its pixel size on a form carries no information the
        // person filling it in can act on. What carries meaning is how many
        // blocks there are and how they are GROUPED, and that survives
        // normalisation intact.
        //
        // The document's real geometry is answered by the flattened preview,
        // which renders through PDFBox and cannot be wrong about it. This
        // surface is for entering data.
        const BLOCK_W = 32;
        const BLOCK_H = 40;
        const GROUP_GAP = 18;   // visible break at a configured gap position

        return (
          <div className="flex flex-wrap items-center gap-1">
            {Array.from({ length: totalBlocks }, (_, blockIndex) => {
              const blockValue = value[blockIndex] || '';
              // firstGapPosition means "gap AFTER this index", matching how the
              // old absolute layout added it.
              const breakAfter =
                (firstGap > 0 && blockIndex === firstGapPosition) ||
                (secondGap > 0 && blockIndex === secondGapPosition);
              return (
                <React.Fragment key={blockIndex}>
                  <input
                    type="text"
                    value={blockValue}
                    ref={el => { blockInputRefs.current[`${field.id}:${blockIndex}`] = el; }}
                    onChange={(e) => {
                      const typed = e.target.value;
                      const newValue = value.split('');
                      newValue[blockIndex] = typed.slice(-1); // Only allow 1 character
                      handleFieldChange(field, newValue.join(''));

                      // Advance via a ref, after the commit. This used to read
                      // e.currentTarget.parentElement.children AFTER dispatching
                      // the state update - currentTarget is only valid during
                      // the handler, and the focus call raced the re-render it
                      // had just scheduled, so the jump silently did not happen.
                      if (typed && blockIndex < totalBlocks - 1) {
                        const next = blockInputRefs.current[`${field.id}:${blockIndex + 1}`];
                        requestAnimationFrame(() => next?.focus());
                      }
                    }}
                    onKeyDown={(e) => {
                      // Backspace in an empty box steps back. Also by ref: the
                      // boxes are no longer siblings in DOM order, since a gap
                      // inserts a spacer between them.
                      if (e.key === 'Backspace' && !e.currentTarget.value && blockIndex > 0) {
                        blockInputRefs.current[`${field.id}:${blockIndex - 1}`]?.focus();
                      }
                    }}
                    maxLength={1}
                    aria-label={`${fieldLabel} character ${blockIndex + 1} of ${totalBlocks}`}
                    className={`text-center border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      hasError ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                    }`}
                    style={{
                      width: `${BLOCK_W}px`,
                      height: `${BLOCK_H}px`,
                      // border-box, no padding, unit leading: an input's default
                      // padding and line-height would otherwise eat the content
                      // area and clip the character.
                      boxSizing: 'border-box',
                      padding: 0,
                      // Line box = the full content height (box minus its 1px
                      // borders), so the glyph centres in the cell.
                      //
                      // This was lineHeight: 1, which sat the character
                      // slightly high: a line box exactly the font size is
                      // shorter than the font's own ascent plus descent, and
                      // the browser centres the LINE BOX rather than the glyph.
                      // It was there to stop clipping back when the box took
                      // its height from the PDF; at a fixed 40px it only
                      // introduced the offset.
                      lineHeight: `${BLOCK_H - 2}px`,
                      fontSize: '18px',
                    }}
                  />
                  {breakAfter && <span aria-hidden="true" style={{ width: `${GROUP_GAP}px` }} />}
                </React.Fragment>
              );
            })}
          </div>
        );

      default:
        return (
          <input
            type="text"
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            placeholder={field.formatHint || `Enter ${fieldLabel}`}
            className={baseInputClasses} style={fontStyle}
          />
        );
    }
  };

  if (fields.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <div className="text-gray-400 mb-4">
          <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Form Fields Selected</h3>
        <p className="text-gray-600">
          Select fields from the PDF viewer to see a live preview of your form here.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* Form Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">
              {formName && formName !== "Generated Form - undefined" ? formName : "Your Interactive Form"}
            </h2>
            <p className="text-blue-100 mt-1">
              Live Form Preview • {fields.length} field{fields.length !== 1 ? 's' : ''} • Test your form below
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="bg-white bg-opacity-20 px-3 py-1 rounded-full">
              <span className="text-white text-sm font-medium">Interactive</span>
            </div>
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" title="Live form ready for testing"></div>
          </div>
        </div>
        
        {/* Guidance Text */}
        <div className="mt-4 bg-white bg-opacity-10 rounded-lg p-3">
          <p className="text-blue-100 text-sm">
            💡 <strong>How to use:</strong> Fill out the form fields below, then use the buttons at the bottom to download a fillable or flattened PDF with your data, or to generate the Spring Boot project.
          </p>
          {process.env.NODE_ENV === 'development' && (
            <p className="text-blue-100 text-xs mt-2">
              <strong>Debug:</strong> Fields count: {fields.length} • Last render: {new Date().toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>

      {/* Form Content: field-list sidebar + entry form */}
      <div className="flex gap-4 items-start p-6">
        <FieldListSidebar
          fields={fields}
          selectedFieldId={focusedFieldId}
          onSelectField={handleSidebarFieldSelect}
          badgeById={previewBadgeById}
          displayNameOf={sidebarDisplayName}
          prefs={fieldSortPrefs}
          onPrefsChange={onFieldSortPrefsChange}
          readingOrderMode={readingOrderMode}
          collapseStorageKey="pfbPreviewFieldListCollapsed"
        />
      <form onSubmit={handleSubmit} className="flex-1 min-w-0">
        <div className="space-y-6">
          {orderedFields.map((field, index) => {
            return (
            <div
              key={field.id}
              ref={el => { fieldCardRefs.current[field.id] = el; }}
              className={`p-4 border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow ${
                focusedFieldId === field.id ? 'border-green-400 ring-2 ring-green-300' : 'border-gray-200'
              }`}>
              {/* Field Header with comprehensive information */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <label 
                      htmlFor={field.id} 
                      className="text-lg font-semibold text-gray-900 truncate"
                    >
                      {field.name === 'loading...' || !field.name || field.name.startsWith('new_field_')
                        ? `Field ${fields.indexOf(field) + 1}` 
                        : field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                      }
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      field.type === 'character_block' ? 'bg-purple-100 text-purple-800' :
                      field.type === 'checkbox' ? 'bg-green-100 text-green-800' :
                      field.type === 'select' ? 'bg-blue-100 text-blue-800' :
                      field.type === 'date' ? 'bg-orange-100 text-orange-800' :
                      field.type === 'email' ? 'bg-pink-100 text-pink-800' :
                      field.type === 'number' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {field.type}
                    </span>
                  </div>
                  
                  {/* Field Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600 mb-2">
                    <div>
                      <span className="font-medium">ID:</span> {field.id}
                    </div>
                    <div>
                      <span className="font-medium">Page:</span> {field.pageNumber}
                    </div>
                    <div>
                      <span className="font-medium">Position:</span> ({Math.round(field.x)}, {Math.round(field.y)})
                    </div>
                    <div>
                      <span className="font-medium">Size:</span> {Math.round(field.width)} × {Math.round(field.height)}
                    </div>
                  </div>

                  {/* Character Block Details */}
                  {field.type === 'character_block' && field.characterBlockConfig && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-purple-700 bg-purple-50 p-2 rounded mb-2">
                      <div>
                        <span className="font-medium">Blocks:</span> {field.characterBlockConfig.totalBlocks}
                      </div>
                      <div>
                        <span className="font-medium">Block Size:</span> {field.characterBlockConfig.blockWidth || 30} × {field.characterBlockConfig.blockHeight || 40}
                      </div>
                      <div>
                        <span className="font-medium">Spacing:</span> {field.characterBlockConfig.blockSpacing ?? 5}px
                      </div>
                      {field.characterBlockConfig.firstGapPosition !== undefined && (
                        <div>
                          <span className="font-medium">Gap 1:</span> After pos {field.characterBlockConfig.firstGapPosition} (+{field.characterBlockConfig.firstGap || 0}px)
                        </div>
                      )}
                      {field.characterBlockConfig.secondGapPosition !== undefined && (
                        <div>
                          <span className="font-medium">Gap 2:</span> After pos {field.characterBlockConfig.secondGapPosition} (+{field.characterBlockConfig.secondGap || 0}px)
                        </div>
                      )}
                      {/* DERIVED from the groups, never read off the config. The stored
                          groupPattern was a second copy of this same fact and drifted from
                          it, so showing it here reported a division the PDF would not
                          actually have (defect D3). */}
                      <div>
                        <span className="font-medium">Pattern:</span>{' '}
                        {characterBlockPattern(resolveCharacterBlockGroups(field.characterBlockConfig))}
                      </div>
                    </div>
                  )}

                  {/* Default Value and Placeholder */}
                  {(field.defaultValue || field.formatHint) && (
                    <div className="text-xs text-gray-600 mb-2">
                      {field.defaultValue && (
                        <div><span className="font-medium">Default:</span> {field.defaultValue}</div>
                      )}
                      {field.formatHint && (
                        <div><span className="font-medium">Format hint:</span> {field.formatHint}</div>
                      )}
                    </div>
                  )}
                </div>
                
              </div>

              {/* Field Input */}
              {renderField(field)}

              {/* Field Error */}
              {validationErrors[field.id] && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-600 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {validationErrors[field.id]}
                  </p>
                </div>
              )}
            </div>
            );
          })}
        </div>

        {/* Form Actions */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          {/* Only show generation section if generation functions are provided */}
          {(onGenerateFillablePDF || onGenerateFlattenedPDF || onGenerateSpringBootProject) && (
            <>
              {/* Action Buttons Help Text */}
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">What would you like to do?</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-blue-800">
                  {onGenerateFillablePDF && (
                    <div>
                      <strong>📝 Generate Fillable PDF:</strong> Creates a downloadable PDF with editable form fields
                    </div>
                  )}
                  {onGenerateFlattenedPDF && (
                    <div>
                      <strong>📄 Generate Flattened PDF:</strong> Creates a PDF with permanent text (fields become non-editable)
                    </div>
                  )}
                  {onPreviewFlattenedPDF && (
                    <div>
                      <strong>👁️ Preview flattened PDF:</strong> Shows how the finished PDF will look, rendered on the server
                    </div>
                  )}
                  {onGenerateSpringBootProject && (
                    <div>
                      <strong>🚀 Generate Spring Boot Project:</strong> Creates a complete Spring Boot application with REST API
                    </div>
                  )}
                  <div>
                    <strong>🔍 Preview submission JSON:</strong> Shows the exact data payload a submission sends — a sample of what the generated project's API receives
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                {/* Equal-width 2x2 action grid (stacks on narrow screens):
                    PDF outputs on top, project generation + test below. */}
                {onPreviewFlattenedPDF && (
                  <button
                    type="button"
                    onClick={() => onPreviewFlattenedPDF(formData)}
                    disabled={isGeneratingFillablePDF || isGeneratingFlattenedPDF || isGeneratingSpringBootProject}
                    title="See exactly how the finished PDF will look - nothing is downloaded"
                    className="w-full flex justify-center items-center px-6 py-3 rounded-lg font-semibold border-2 border-indigo-600 text-indigo-700 bg-white hover:bg-indigo-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Preview flattened PDF
                  </button>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {onGenerateFillablePDF && (
                    <button
                      type="button"
                      onClick={() => guard('fillable', handleGenerateFillablePDF)}
                      disabled={isGeneratingFillablePDF || isGeneratingFlattenedPDF || isGeneratingSpringBootProject}
                      title="Generate and download a fillable PDF with editable form fields - the PDF will be saved to your Downloads folder"
                      // self-start is load-bearing. This grid is align-items:stretch,
                      // and the Flattened cell beside this one carries its button PLUS
                      // an always-mounted live region, so that cell is ~1.25rem taller.
                      // Without this, a bare grid item stretches to match and this button
                      // renders at nearly twice the height of the one next to it.
                      className={`w-full self-start flex justify-center px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                        isGeneratingFillablePDF || isGeneratingFlattenedPDF || isGeneratingSpringBootProject
                          ? 'bg-gray-400 text-white cursor-not-allowed' 
                          : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105'
                      }`}
                    >
                      {isGeneratingFillablePDF ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Generating Fillable PDF...
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Generate Fillable PDF
                          <GateLock show={!allows('fillable')} />
                        </div>
                      )}
                    </button>
                  )}

                  {onGenerateFlattenedPDF && (
                    <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => {
                        // `aria-disabled`, so the click ARRIVES here rather than
                        // being swallowed. Refusing in silence is what a real
                        // `disabled` would do; this refuses and then puts the
                        // reader on the field that caused the refusal.
                        if (hasEmptyRequired) {
                          setPdfGenerationStatus({ message: emptyRequiredSentence, type: 'error' });
                          focusFirstEmptyRequired();
                          return;
                        }
                        guard('flattened', handleGenerateFlattenedPDF);
                      }}
                      // The REAL `disabled` is kept for the in-flight state
                      // only: that is transient, unactionable, and the label
                      // already announces it. An empty required field is a
                      // PRECONDITION the user can still meet, so the control
                      // must stay in the tab order and keep announcing itself -
                      // the same argument ArtifactGate makes for its own
                      // buttons ("a disabled control gives no feedback on click
                      // and is skipped by screen readers", section 3.5.4).
                      // PFB's Save button sets the weaker precedent - `disabled`
                      // plus a title - and is deliberately not followed here.
                      disabled={isGeneratingFillablePDF || isGeneratingFlattenedPDF || isGeneratingSpringBootProject}
                      aria-disabled={hasEmptyRequired}
                      aria-describedby="flattened-required-note"
                      title="Generate and download a flattened PDF with permanent text (fields become non-editable) - the PDF will be saved to your Downloads folder"
                      // Dimmed, but NOT by `opacity-50` over the solid indigo:
                      // white on half-strength indigo lands around 2:1. WCAG
                      // 1.4.3 exempts INACTIVE controls, and an `aria-disabled`
                      // button is not inactive - it is focusable and it does
                      // something when clicked, so it owes the full 4.5:1.
                      // indigo-300 on indigo-900 reads as unavailable at a
                      // glance and measures 5.7:1.
                      className={`w-full flex justify-center px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                        isGeneratingFillablePDF || isGeneratingFlattenedPDF || isGeneratingSpringBootProject
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : hasEmptyRequired
                          ? 'bg-indigo-300 text-indigo-900 cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transform hover:scale-105'
                      }`}
                    >
                      {isGeneratingFlattenedPDF ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Generating Flattened PDF...
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          Generate Flattened PDF
                          <GateLock show={!allows('flattened')} />
                        </div>
                      )}
                    </button>
                    {/* The reason, beside the control it governs.
                        POLITE LIVE REGION, and ALWAYS mounted: the element has
                        to already exist for its text changes to be announced,
                        so the count is heard falling to zero as fields are
                        filled. A region mounted at the moment it first gains
                        text is announced unreliably.
                        It is also the button's accessible description, so the
                        reason arrives WITH the control instead of having to be
                        hunted for - and the sentence itself is the argument for
                        why fillable does not do this. */}
                    <p
                      id="flattened-required-note"
                      aria-live="polite"
                      className="mt-1 text-xs text-amber-700"
                      style={{ minHeight: '1rem' }}
                    >
                      {hasEmptyRequired ? emptyRequiredSentence : ''}
                    </p>
                    </div>
                  )}

                  {onGenerateSpringBootProject && (
                    <button
                      type="button"
                      onClick={() => guard('project', handleGenerateSpringBootProject)}
                      disabled={isGeneratingFillablePDF || isGeneratingFlattenedPDF || isGeneratingSpringBootProject}
                      title="Generate and download a complete Spring Boot project with REST API - the ZIP file will be saved to your Downloads folder"
                      className={`sm:col-span-2 w-full flex justify-center px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                        isGeneratingFillablePDF || isGeneratingFlattenedPDF || isGeneratingSpringBootProject
                          ? 'bg-gray-400 text-white cursor-not-allowed' 
                          : 'bg-purple-600 text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transform hover:scale-105'
                      }`}
                    >
                      {isGeneratingSpringBootProject ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Generating Spring Boot Project...
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                          </svg>
                          Generate Spring Boot Project
                          <GateLock show={!allows('project')} />
                        </div>
                      )}
                    </button>
                  )}
                  
                </div>

                {/* Sample submission payload — demoted from the old Test Form
                    Submission button: validates, then shows the JSON a
                    submission sends (what the generated project's API receives). */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      if (showSubmissionJson) {
                        setShowSubmissionJson(false);
                      } else if (validateForm()) {
                        setShowSubmissionJson(true);
                      } else {
                        setPdfGenerationStatus({
                          message: 'Please fix the validation errors to preview the submission JSON',
                          type: 'error'
                        });
                      }
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 underline underline-offset-2"
                  >
                    {showSubmissionJson ? 'Hide submission JSON' : 'Preview submission JSON'}
                  </button>
                </div>
                {showSubmissionJson && (
                  <div className="p-3 bg-gray-900 rounded-lg text-left">
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <span className="text-xs font-semibold text-gray-300">
                        Sample payload — what the generated project's API receives on submit
                      </span>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(JSON.stringify(formData, null, 2))}
                        title="Copy JSON to clipboard"
                        className="px-2 py-1 text-xs font-medium text-gray-200 bg-gray-700 hover:bg-gray-600 rounded flex-shrink-0"
                      >
                        Copy
                      </button>
                    </div>
                    <pre className="text-xs text-green-300 overflow-auto max-h-64">
                      {JSON.stringify(formData, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Live preview with real-time validation
          </div>
        </div>

        {/* PDF Generation Status */}
        {pdfGenerationStatus.message && (
            <div className={`mt-4 p-3 rounded-lg border ${
              pdfGenerationStatus.type === 'success' 
                ? 'bg-green-50 border-green-200 text-green-800' 
                : pdfGenerationStatus.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}>
              <div className="flex items-center">
                {pdfGenerationStatus.type === 'success' && (
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
                {pdfGenerationStatus.type === 'error' && (
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
                {pdfGenerationStatus.type === 'info' && (
                  <svg className="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
                <span className="text-sm font-medium">{pdfGenerationStatus.message}</span>
              </div>
            </div>
          )}

        {/* Form Data Debug (Development) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-6 p-4 bg-gray-100 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Form Data (Debug)</h4>
            <pre className="text-xs text-gray-600 overflow-auto">
              {JSON.stringify(formData, null, 2)}
            </pre>
          </div>
        )}
      </form>
      </div>

    </div>
  );
};

export default LiveFormPreview;
